<script setup lang="ts">
import Select from '@/components/atoms/Select.vue';
import { useTheme, isThemePreference } from '@/composables/useTheme.js';

// App-chrome theme switch (SD-054 §C.2): light / dark / match-system, wired to
// `useTheme`. A thin composition — the pure `Select` atom renders the field,
// `useTheme` holds the state; this switch owns the preference→label mapping and
// calls the setter. The atom stays theme-agnostic. (Distinct from the domain
// `baselines/ThemeSelector.vue`, which picks a screenshot theme facet.)
const { preference, setPreference } = useTheme();

// The atom emits a raw string; the guard narrows it to a ThemePreference. The
// options only ever emit valid values, so the guard always passes — it just
// keeps the setter call type-safe without a cast.
function onSelect(value: string): void {
  if (isThemePreference(value)) setPreference(value);
}
</script>

<template>
  <Select
    class="theme-switch"
    :model-value="preference"
    aria-label="Theme"
    @update:model-value="onSelect"
  >
    <option value="light">Light</option>
    <option value="dark">Dark</option>
    <option value="system">Match system</option>
  </Select>
</template>

<style scoped>
/* The field box (fill/border/radius/focus ring) comes from the `Select` atom;
 * only this control's padding + text colour survive here, disjoint from the
 * atom's props so the composition stays pixel-clean (the atom pattern). */
.theme-switch {
  padding: var(--space-1) var(--space-2);
  color: var(--color-text-primary);
}
</style>
